import { UseQueryOptions } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { Gateway, GatewayState } from 'types/gateway';
import { OffsetPaginated, OffsetPaginationParams } from 'types/pagination';
import { TagParams } from 'tags/types/tagged';

import { useAPI } from 'hooks/api/useAPI';
import { useAuthenticatedQuery } from 'hooks/api/useAuthenticatedQuery';
import { useUser } from 'hooks/useAuth';
import { updateWorkspaceBooleanTrait } from 'services/customer_tracking';

export type UseGatewaysParams = TagParams & {
  statuses?: GatewayState[];
  gateway_ids?: string[];
  gateway_models?: string[];
  gateway_names?: string[];
  gateway_local_ips?: string[];
  gateway_versions?: string[];
  /** ISO date string */
  created_ts_since?: string;
  /** ISO date string */
  created_ts_until?: string;
  /** ISO date string */
  updated_ts_since?: string;
  /** ISO date string */
  updated_ts_until?: string;
};

type UseGatewaysOptions = UseQueryOptions<
  OffsetPaginated<Gateway>,
  APIError
> & {
  params?: UseGatewaysParams & OffsetPaginationParams;
};

export function useGateways({
  queryKey = ['gateways'],
  params,
  ...options
}: UseGatewaysOptions = {}) {
  const { applicationID, client } = useAPI();
  const user = useUser();

  const queryParams = {
    pagination: 'offset',
    page: 1,
    limit: 50,
    ...params,
  };

  return useAuthenticatedQuery(
    [...queryKey, applicationID, queryParams],
    async () => {
      const { data } = await client.get<OffsetPaginated<Gateway>>(
        `/apps/${applicationID}/gateways`,
        { params: queryParams }
      );

      return data;
    },
    {
      ...options,
      onSuccess(data) {
        if (!user) {
          return;
        }

        updateWorkspaceBooleanTrait({
          userId: user.id,
          workspaceId: applicationID,
          trait: 'has_gateway',
          value: data.data.length > 0,
        });
      },
    }
  );
}
